"use client";

import { Fragment, type ReactNode } from "react";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import Link from "@/components/ui/link";
import { normalizeTypedUrl } from "@/lib/feed/rich-content";
import {
  mentionColorClass,
  type ResolvedMention,
  resolveMentionHref,
  segmentTextByMentions,
} from "@/lib/mentions";
import { cn } from "@/lib/utils";

const URL_PATTERN = /((?:https?:\/\/|www\.)[^\s<]+)/gi;
const TRAILING_URL_PUNCTUATION = /[.,!?;:)]*$/;
const HASHTAG_PATTERN = /(^|[\s])#([A-Za-z0-9_]{1,40})/g;
const LINE_CLAMP_CLASS = {
  1: "line-clamp-1",
  2: "line-clamp-2",
  3: "line-clamp-3",
  4: "line-clamp-4",
  5: "line-clamp-5",
  6: "line-clamp-6",
  8: "line-clamp-8",
  10: "line-clamp-10",
} as const;

type RichPostTextProps = {
  content: string;
  mentions?: ResolvedMention[];
  authorName?: string | null;
  authorHref?: string | null;
  className?: string;
  textClassName?: string;
  lineClamp?: keyof typeof LINE_CLAMP_CLASS;
  linkMentions?: boolean;
  linkUrls?: boolean;
};

function renderTextWithHashtags(value: string, keyPrefix: string) {
  const nodes: ReactNode[] = [];
  let cursor = 0;

  HASHTAG_PATTERN.lastIndex = 0;
  let match = HASHTAG_PATTERN.exec(value);
  while (match !== null) {
    const full = match[0];
    const prefix = match[1] ?? "";
    const tag = match[2] ?? "";
    const hashtagStart = match.index + prefix.length;

    if (hashtagStart > cursor) {
      nodes.push(
        <Fragment key={`${keyPrefix}-hash-text-${cursor}`}>
          {value.slice(cursor, hashtagStart)}
        </Fragment>,
      );
    }

    nodes.push(
      <Link
        key={`${keyPrefix}-hash-${hashtagStart}`}
        href={`/feed?hashtag=${encodeURIComponent(tag.toLowerCase())}`}
        className="font-semibold text-brand hover:underline"
        onClick={(event) => event.stopPropagation()}
      >
        #{tag}
      </Link>,
    );

    cursor = match.index + full.length;
    match = HASHTAG_PATTERN.exec(value);
  }

  if (cursor < value.length) {
    nodes.push(
      <Fragment key={`${keyPrefix}-hash-text-${cursor}`}>
        {value.slice(cursor)}
      </Fragment>,
    );
  }

  return nodes.length > 0 ? (
    nodes
  ) : (
    <Fragment key={keyPrefix}>{value}</Fragment>
  );
}

function renderTextWithLinks(
  value: string,
  keyPrefix: string,
  linkUrls: boolean,
) {
  if (!linkUrls) {
    return <Fragment key={keyPrefix}>{value}</Fragment>;
  }

  const nodes: ReactNode[] = [];
  let cursor = 0;

  URL_PATTERN.lastIndex = 0;
  let match = URL_PATTERN.exec(value);
  while (match !== null) {
    const matched = match[0];
    const start = match.index;

    if (start > cursor) {
      nodes.push(
        <Fragment key={`${keyPrefix}-text-${cursor}`}>
          {renderTextWithHashtags(
            value.slice(cursor, start),
            `${keyPrefix}-text-${cursor}`,
          )}
        </Fragment>,
      );
    }

    const trailing = matched.match(TRAILING_URL_PUNCTUATION)?.[0] ?? "";
    const urlText = trailing
      ? matched.slice(0, Math.max(0, matched.length - trailing.length))
      : matched;

    if (urlText.length > 0) {
      nodes.push(
        <a
          key={`${keyPrefix}-url-${start}`}
          href={normalizeTypedUrl(urlText)}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-brand hover:underline"
          onClick={(event) => event.stopPropagation()}
        >
          {urlText}
        </a>,
      );
    }

    if (trailing) {
      nodes.push(
        <Fragment key={`${keyPrefix}-url-trailing-${start}`}>
          {trailing}
        </Fragment>,
      );
    }

    cursor = start + matched.length;
    match = URL_PATTERN.exec(value);
  }

  if (cursor < value.length) {
    nodes.push(
      <Fragment key={`${keyPrefix}-text-${cursor}`}>
        {renderTextWithHashtags(
          value.slice(cursor),
          `${keyPrefix}-text-${cursor}`,
        )}
      </Fragment>,
    );
  }

  return nodes.length > 0 ? (
    nodes
  ) : (
    <Fragment key={keyPrefix}>{value}</Fragment>
  );
}

export function RichPostText({
  content,
  mentions,
  authorName,
  authorHref,
  className,
  textClassName,
  lineClamp,
  linkMentions = true,
  linkUrls = true,
}: RichPostTextProps) {
  const segments = segmentTextByMentions(content, mentions);
  const clampClass = lineClamp ? LINE_CLAMP_CLASS[lineClamp] : undefined;

  const author = authorName?.trim() ? (
    authorHref ? (
      <BackAwareLink
        href={authorHref}
        className="font-semibold text-text-primary hover:underline"
      >
        {authorName}
      </BackAwareLink>
    ) : (
      <span className="font-semibold text-text-primary">{authorName}</span>
    )
  ) : null;

  return (
    <p
      className={cn(
        "text-sm leading-relaxed text-text-primary",
        clampClass,
        className,
      )}
    >
      {author ? (
        <>
          {author}
          <span>: </span>
        </>
      ) : null}
      <span className={cn("whitespace-pre-wrap", textClassName)}>
        {segments.map((segment, index) => {
          const segmentKey = `segment-${index}`;

          if (segment.kind === "text") {
            return renderTextWithLinks(segment.value, segmentKey, linkUrls);
          }

          const colorClass = mentionColorClass(segment.mention.type);
          const href = linkMentions
            ? resolveMentionHref(segment.mention)
            : null;
          const mentionClassName = cn(
            "font-semibold",
            colorClass,
            href && "hover:underline",
          );

          return href ? (
            <BackAwareLink
              key={`${segmentKey}-${segment.mention.type}`}
              href={href}
              className={mentionClassName}
            >
              {segment.value}
            </BackAwareLink>
          ) : (
            <span
              key={`${segmentKey}-${segment.mention.type}`}
              className={mentionClassName}
            >
              {segment.value}
            </span>
          );
        })}
      </span>
    </p>
  );
}
